// Avatar — initials in a circle. Default style: slate-900 background with
// white text, matching the BM avatar across the mockups. `tone="muted"`
// switches to stone-200/slate-700 for secondary contexts.
//
// Initials are derived from a name by taking up to two leading characters
// of the first two whitespace-separated tokens. "Ben Meier" → "BM",
// "Antonela" → "AN", "Rocky Mountain Surgery Center" → "RM".

export interface AvatarProps {
  name?: string | null;
  /** Override derived initials. */
  initials?: string;
  size?: 'sm' | 'md' | 'lg';
  tone?: 'dark' | 'muted';
  className?: string;
}

const SIZE_PX: Record<NonNullable<AvatarProps['size']>, number> = {
  sm: 28,
  md: 32,
  lg: 40,
};

export function Avatar({
  name,
  initials,
  size = 'md',
  tone = 'dark',
  className,
}: AvatarProps) {
  const dim = SIZE_PX[size];
  const text = (initials ?? deriveInitials(name)).slice(0, 2).toUpperCase();
  const toneClass =
    tone === 'muted'
      ? 'bg-tcs-border text-tcs-text-secondary'
      : 'bg-tcs-inverse text-tcs-inverse-ink';
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full text-xs font-semibold ${toneClass} ${className ?? ''}`}
      style={{ width: dim, height: dim }}
      aria-label={name ?? 'User'}
      title={name ?? undefined}
    >
      {text || '·'}
    </span>
  );
}

function deriveInitials(name: string | null | undefined): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2);
  return parts[0][0] + parts[1][0];
}
